import { toBoolOrNull, toIntOrNull } from '@utils/parse';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import { DashRegistrationStep } from '@/types/dashboard';
import { defaultScreeningProgress } from '@/types/screening/screening-progress';
import type {
    IPregnancyOrNursingFormValues,
    IPregnancyOrNursingModel,
    IPregnancyOrNursingSubmitResult,
} from '@/types/screening/pregnancy-or-nursing';

const PREGNANCY_OR_NURSING_ENDPOINT = '/api/register/pregnancy-or-nursing';

const defaultPregnancyOrNursingModel: IPregnancyOrNursingModel = {
    ...defaultScreeningProgress,
    currentRegistrationStep: DashRegistrationStep.PregnantOrNursing,
    hasBeenPregnant: null,
    isPregnant: null,
    monthsSincePregnant: null,
    hadGestationalDiabetes: null,
    isNursing: null,
    isPlanningOnPregnancy: null,
    isTakingBirthControl: null,
    birthControlForm: '',
    birthControlMonths: null,
    birthControlComplications: '',
    isAbleToStopBirthControl: null,
};

export const usePregnancyOrNursing = (registrationId: number, editKey: string) => {
    const url = `${PREGNANCY_OR_NURSING_ENDPOINT}/${registrationId}/${editKey}`;

    const { model, isLoading, error } = useApiModel(url, defaultPregnancyOrNursingModel);

    const { submit, isSubmitting, submitError } = useApiSubmit<
        IPregnancyOrNursingFormValues,
        DashRegistrationStep,
        IPregnancyOrNursingSubmitResult
    >(
        url,
        (values) => {
            // The sub-questions only apply to the answer that reveals them, so the rest are
            // cleared rather than posting values the donor can no longer see.
            const hasBeenPregnant = toBoolOrNull(values.hasBeenPregnant);
            const isPregnant = hasBeenPregnant === true ? toBoolOrNull(values.isPregnant) : null;
            const isTakingBirthControl = toBoolOrNull(values.isTakingBirthControl);

            return {
                hasBeenPregnant,
                isPregnant,
                monthsSincePregnant: isPregnant === false ? toIntOrNull(values.monthsSincePregnant) : null,
                hadGestationalDiabetes: hasBeenPregnant === true ? toBoolOrNull(values.hadGestationalDiabetes) : null,
                isNursing: hasBeenPregnant === true ? toBoolOrNull(values.isNursing) : null,
                isPlanningOnPregnancy: toBoolOrNull(values.isPlanningOnPregnancy),
                isTakingBirthControl,
                birthControlForm: isTakingBirthControl === true ? values.birthControlForm : '',
                birthControlMonths: isTakingBirthControl === true ? toIntOrNull(values.birthControlMonths) : null,
                birthControlComplications: isTakingBirthControl === true ? values.birthControlComplications : '',
                isAbleToStopBirthControl: isTakingBirthControl === true
                    ? toBoolOrNull(values.isAbleToStopBirthControl)
                    : null,
            };
        },
        (nextStep) => ({ nextStep }),
    );

    return { model, isLoading, error, submit, isSubmitting, submitError };
};
